import { Box, Chip, Tooltip, } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import _ from 'lodash'
import { ROWS_PER_PAGE_OPTIONS } from 'constants'
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { useEffect, useState, useRef } from "react";
import { search, setSearchApplicationQuery, setSearchApplicationLimit, setSearchApplicationOffset, resetSearchApplicationQuery } from "store/application/student-applications";
import { useDispatch, useSelector } from "react-redux";
import { useSession } from "components/Providers";

export default function ApplicationRecordsList({ queryReady = true }) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const { district } = useSession();
    const query = useSelector(state => state.application.studentApplications.query)
    const { list, count, recordsLoading } = useSelector(state => state.application.studentApplications)
    const showStatusOptions = query?.status === "inprogress";
    const types = useSelector(state => state.lookup.types)?.filter(item => item.category === 'applicationStatus')

    const filterNullValues = obj => {
        let temp = {}
        Object.keys(obj).forEach(key => {
            obj[key] !== null && obj[key] !== '' ? (temp[key] = obj[key]) : null
        })
        return temp
    }

    const basePath = "/admission/application-list";
    const isInFeature = (p) => (p || "").startsWith(basePath);

    const wasInFeatureRef = useRef(isInFeature(location.pathname));

    useEffect(() => {
        wasInFeatureRef.current = isInFeature(location.pathname);
    }, [location.pathname]);

    useEffect(() => {
        return () => {
            if (wasInFeatureRef.current) {
                dispatch(resetSearchApplicationQuery());
            }
        };
    }, [dispatch]);

    useEffect(() => {
        if (!queryReady) return;
        dispatch(search({ ...filterNullValues(query) }));
    }, [queryReady, query?.limit, query?.offset, query?.sortBy, query?.sortOrder, dispatch]);

    const tableColumns = [
        {
            field: 'name',
            headerName: t('Full Name'),
            editable: false,
            sortable: true,
            flex: 1,
            renderCell: params => (
                <Box sx={{ cursor: 'pointer' }}>{`${params.row.lastName || ' '}, ${params.row.firstName || ' '}`}</Box>
            )
        },
        {
            field: 'gradeApplying',
            headerName: t('Grade Applying'),
            editable: false,
            sortable: true,
            flex: 1,
            renderCell: (params) => params.row.studentGr.name || ' '
        },
        {
            field: 'applicationNumber',
            headerName: t('Application #'),
            editable: false,
            sortable: false,
            flex: 1,
            renderCell: (params) => params.row.id || ' '
        },
        {
            field: 'schoolName',
            headerName: t('School Name'),
            editable: false,
            sortable: true,
            flex: 1,
            renderCell: (params) => {
                const schools = params.row.applicationSchools || [];
                if (schools.length === 0) return ' ';
                const firstSchool = schools[0]?.school?.abbreviation || ' ';
                const restSchools = schools.slice(1).map(s => s.school?.abbreviation).filter(Boolean);
                if (restSchools.length === 0) {
                    return firstSchool;
                }
                return (
                    <Tooltip title={restSchools.join(', ')}>
                        <span style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}>
                            {firstSchool}
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            field: 'applicationDate',
            headerName: t('Application Date'),
            editable: false,
            sortable: true,
            flex: 1,
            renderCell: (params) => dayjs(params.row.createdAt).format('MM/DD/YYYY') || ' '
        },
        !district?.isServiceProvider ? {
            field: 'sibling',
            headerName: t('Sibling'),
            editable: false,
            sortable: true,
            flex: 1,
            renderCell: (params) => params?.row?.sibling ? 'Yes' : 'No'
        } : null,
        !district?.isServiceProvider ? {
            field: 'lotteryStatus',
            headerName: t('Lottery Status'),
            editable: false,
            sortable: false,
            flex: 1,
            renderCell: params => (
                <Chip size="small" color="primary" label={types?.find(type => type.code === params?.row?.applicationSchools[0]?.status)?.name} />
            )
        } : null,
        !district?.isServiceProvider ? {
            field: 'lotteryPlacement',
            headerName: t('Lottery Placement'),
            editable: false,
            sortable: true,
            flex: 1,
            renderCell: params => {
                const school = params?.row?.applicationSchools?.[0];
                const lotteryRank = school?.lotteryRank;
                const lotteryStatus = school?.lotteryStatus;
                const value = (lotteryRank != null && lotteryStatus != null)
                    ? (`${lotteryStatus}${lotteryRank}`)
                    : null;
                return (<Chip size="small" color="default" label={value != null ? value : 'Not Available'} />);
            }
        } : null,
    ].filter(Boolean)

    const handleCellClick = params => {
        navigate(`/admission/application-list/${params.row?.id}`)
    }

    const pageSizeOptions = _.uniq([...ROWS_PER_PAGE_OPTIONS])

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    })

    const onPaginationModelChange = params => {
        if (params.pageSize !== paginationModel?.pageSize) {
            dispatch(setSearchApplicationLimit(params.pageSize))
            dispatch(setSearchApplicationOffset(0))
            setPaginationModel({ ...paginationModel, ...params, page: 0 })
        } else {
            dispatch(setSearchApplicationLimit(params.pageSize))
            dispatch(setSearchApplicationOffset(params.pageSize * params.page))
            setPaginationModel({ ...paginationModel, ...params })
        }
    }

    const handleSortModelChange = params => {
        if (params.length > 0) {
            const sortBy = params[0]?.field
            const sortOrder = params[0]?.sort
            dispatch(setSearchApplicationQuery({ ...query, sortBy, sortOrder }))
        }
    }

    return (
        <Box width="100%" sx={{ overflowX: 'auto' }}>
            <Box minWidth="600px">
                <DataGrid
                    onRowClick={handleCellClick}
                    rows={list}
                    columns={tableColumns}
                    rowCount={count}
                    paginationMode="server"
                    loading={recordsLoading}
                    paginationModel={paginationModel}
                    onPaginationModelChange={onPaginationModelChange}
                    onSortModelChange={handleSortModelChange}
                    pageSizeOptions={pageSizeOptions}
                    page={query?.offset / query?.limit}
                    sx={{ height: showStatusOptions ? 'calc(100vh - 355px)' : 'calc(100vh - 310px)', overflow: 'auto' }}
                    slotProps={{ loadingOverlay: { variant: 'circular-progress', noRowsVariant: 'circular-progress'} }}
                    pagination
                    disableColumnMenu
                />
            </Box>
        </Box>
    )
}